import { useEffect, useState } from 'react';
import _ from 'lodash';
import { matchPath, useNavigate, useLocation } from 'react-router';

import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import { logout } from '@/patient-app-common/slices/authSlice';
import { getURLParam } from '@/patient-app-common/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

export default function useSetupPrompt() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [initUrl, setInitUrl] = useState('');
  const [pathToSetup, setPathToSetup] = useState('');

  const { passwordResetToken, patientId, providerMode, setupUrl, directMode } =
    useAppSelector(({ auth }) => auth);

  const { ehrMode } = useAppSelector(({ webApp }) => webApp);

  // Check URL param directly as fallback in case directMode isn't set in Redux yet
  const urlDirectMode = getURLParam(location.search, 'mode') === 'direct';
  const isDirectMode = directMode || urlDirectMode;

  const { data: patient } = useGetPatientQuery(undefined, {
    skip: !patientId || providerMode,
  });

  useEffect(() => {
    setInitUrl(window.location.href);
  }, []);

  useEffect(() => {
    if (ehrMode) return;
    if (isDirectMode) return; // Don't redirect to setup for direct survey links

    if (!_.isEmpty(setupUrl)) {
      setPathToSetup(setupUrl.split('#')[1]);
    } else if (!_.isEmpty(passwordResetToken)) {
      setPathToSetup(`/reset_password_form/${passwordResetToken}`);
    }
  }, [ehrMode, isDirectMode, passwordResetToken, setupUrl]);

  useEffect(() => {
    if (pathToSetup) {
      navigate(pathToSetup, { replace: true });
      dispatch(logout());
    }
  }, [pathToSetup]);

  useEffect(() => {
    if (!patient) return;
    if (isDirectMode) return; // Skip profile confirmation for direct survey links

    const confirmedProfile = localStorage.getItem('confirmedProfile') || '';

    if (patient.signInCount <= 1 && !confirmedProfile) {
      return navigate('/confirm_profile');
    }
  }, [patient, isDirectMode]);

  useEffect(() => {
    if (!patient) return;
    if (isDirectMode) return; // Skip setup survey redirect for direct survey links

    const confirmedProfile =
      localStorage.getItem('confirmedProfile') || patient.signInCount > 1;

    if (!confirmedProfile) return;

    const { setupSurveyId, setupSurveyCompleted } = patient;

    if (!setupSurveyId || setupSurveyCompleted) return;

    const routeParams = {};

    const surveyId = getURLParam(initUrl, 'procedure_id');
    const path = initUrl.split('#')[1];

    const autoAuthLink =
      !_.isEmpty(path) &&
      matchPath(
        {
          path: '/auth_auto/:token/:eventId?',
        },
        path
      );

    const hasNextSurvey =
      autoAuthLink && surveyId && setupSurveyId !== _.toNumber(surveyId);

    if (hasNextSurvey) {
      const eventId = autoAuthLink?.params?.eventId;
      const nextSurveyEventId = eventId !== '0' ? eventId : null;

      routeParams.state = {
        hasNextSurvey,
        nextSurveyEventId,
        nextSurveyPathname: `/tracker/${surveyId}`,
      };
    }

    return navigate(`/tracker/${setupSurveyId}`, routeParams);
  }, [patient, isDirectMode]);
}
